import React, { useState } from 'react';
import { Button, ProgressBar, Image, Card, Alert, Modal } from 'react-bootstrap';
import { ref as storageRef, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../firebase';
import { useAuth } from '../contexts/AuthContext';

interface FileUploaderProps {
  fileType: 'bikePicture' | 'bill'; // Type of file being uploaded
  bikeId: string;
  existingUrl?: string;
  onUploadComplete: (url: string) => void;
  onUploadError?: (error: string) => void;
  onDelete?: () => void;
}

const FileUploader: React.FC<FileUploaderProps> = ({ 
  fileType, 
  bikeId, 
  existingUrl, 
  onUploadComplete, 
  onUploadError,
  onDelete 
}) => {
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState<boolean>(false);
  const { currentUser } = useAuth();

  const allowedFileTypes = {
    bikePicture: ['image/jpeg', 'image/png', 'image/webp', 'image/heic'],
    bill: ['application/pdf']
  };

  const getTypeLabel = () => {
    return fileType === 'bikePicture' ? 'Velo-Bild' : 'Rechnung';
  };
  
  const getAcceptTypes = () => {
    return fileType === 'bikePicture' ? '.jpg,.jpeg,.png,.webp,.heic' : '.pdf';
  };
  
  const getFileTypeDescription = () => {
    return fileType === 'bikePicture' ? 'JPG, PNG, WEBP, HEIC' : 'PDF';
  };

  const handleFileSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Check if file type is allowed
    if (!allowedFileTypes[fileType].includes(file.type)) {
      setError(`Nicht unterstützter Dateityp. Erlaubte Formate für ${getTypeLabel()}: ${getFileTypeDescription()}`);
      return;
    }

    // Reset states
    setError(null);
    setIsUploading(true);
    setUploadProgress(0);

    // Create storage reference
    const fileName = `${Date.now()}_${file.name}`;
    const fileStorageRef = storageRef(
      storage, 
      `bikes/${currentUser?.uid}/${bikeId}/${fileType}/${fileName}`
    );

    // Start upload
    const uploadTask = uploadBytesResumable(fileStorageRef, file);

    // Listen for state changes
    uploadTask.on(
      'state_changed',
      (snapshot) => {
        const progress = Math.round(
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100
        );
        setUploadProgress(progress);
      },
      (error) => {
        console.error('Upload error:', error);
        setError('Fehler beim Hochladen. Bitte versuche es erneut.');
        setIsUploading(false);
        if (onUploadError) onUploadError('Fehler beim Hochladen');
      },
      async () => {
        // Upload completed successfully
        try {
          const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);
          onUploadComplete(downloadUrl);
          setIsUploading(false);
        } catch (error) {
          console.error('Error getting download URL:', error);
          setError('Fehler beim Abrufen der Datei-URL');
          setIsUploading(false);
          if (onUploadError) onUploadError('Fehler beim Abrufen der Datei-URL');
        }
      }
    );
  };

  const handleDelete = async () => {
    if (!existingUrl || !onDelete) return;
    
    try {
      // Extract the path from the URL
      const fileUrlPath = existingUrl.split('?')[0].split('/o/')[1];
      if (!fileUrlPath) throw new Error('Invalid file URL');
      
      const decodedPath = decodeURIComponent(fileUrlPath);
      const fileRef = storageRef(storage, decodedPath);
      
      await deleteObject(fileRef);
      onDelete();
    } catch (error) {
      console.error('Error deleting file:', error);
      setError('Fehler beim Löschen der Datei');
    }
  };

  const handleOpenModal = () => {
    if (existingUrl) {
      setShowModal(true);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  return (
    <Card className="mb-3">
      <Card.Body>
        <Card.Title>{getTypeLabel()} {existingUrl ? 'ändern' : 'hinzufügen'}</Card.Title>
        
        {error && <Alert variant="danger" className="mt-2">{error}</Alert>}
        
        {existingUrl && fileType === 'bikePicture' && (
          <div className="mb-3">
            <Image 
              src={existingUrl} 
              alt="Bike" 
              thumbnail 
              style={{ maxHeight: '200px', maxWidth: '100%', cursor: 'pointer' }} 
              onClick={handleOpenModal}
            />
            <div className="mt-2 text-muted small">Klicken zum Vergrößern</div>
          </div>
        )}
        
        {existingUrl && fileType === 'bill' && (
          <div className="mb-3">
            <div className="d-flex align-items-center">
              <Button 
                variant="link" 
                className="p-0 d-flex align-items-center" 
                onClick={handleOpenModal}
              >
                <i className="bi bi-file-earmark-pdf me-2 fs-1 text-danger"></i>
                <span>Rechnung öffnen</span>
              </Button>
            </div>
          </div>
        )}
        
        {/* Modal for viewing files */}
        <Modal 
          show={showModal} 
          onHide={handleCloseModal} 
          size="lg" 
          centered
          className="file-viewer-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title>{fileType === 'bikePicture' ? 'Velo-Bild' : 'Rechnung'}</Modal.Title>
          </Modal.Header>
          <Modal.Body className="text-center">
            {existingUrl && (
              fileType === 'bill' || existingUrl.toLowerCase().endsWith('.pdf') ? (
                <div className="pdf-container">
                  <div className="ratio ratio-16x9" style={{ minHeight: '500px' }}>
                    <iframe 
                      src={`${existingUrl}#toolbar=1&navpanes=1&scrollbar=1`} 
                      title="PDF Viewer" 
                      width="100%" 
                      height="100%" 
                      frameBorder="0"
                      allowFullScreen
                    >
                      <p>Ihr Browser unterstützt keine eingebetteten PDFs. <a href={existingUrl} target="_blank" rel="noopener noreferrer">PDF herunterladen</a></p>
                    </iframe>
                  </div>
                </div>
              ) : (
                <div className="img-container">
                  <Image 
                    src={existingUrl} 
                    alt="Bike" 
                    style={{ maxWidth: '100%', maxHeight: '70vh' }} 
                  />
                </div>
              )
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Schließen
            </Button>
            {existingUrl && existingUrl.toLowerCase().endsWith('.pdf') && (
              <Button 
                variant="primary" 
                href={existingUrl} 
                target="_blank" 
                rel="noopener noreferrer"
              >
                In neuem Tab öffnen
              </Button>
            )}
          </Modal.Footer>
        </Modal>
        
        {isUploading && (
          <div className="my-3">
            <ProgressBar 
              now={uploadProgress} 
              label={`${uploadProgress}%`} 
              variant="info" 
            />
          </div>
        )}
        
        <div className="d-flex flex-wrap gap-2 mt-2">
          <Button
            as="label"
            htmlFor={`file-upload-${fileType}-${bikeId}`}
            variant="primary"
            disabled={isUploading}
            className="mb-2"
          >
            <i className="bi bi-upload me-2"></i>
            {existingUrl ? 'Ändern' : 'Hochladen'}
            <input
              id={`file-upload-${fileType}-${bikeId}`}
              type="file"
              onChange={handleFileSelection}
              accept={getAcceptTypes()}
              style={{ display: 'none' }}
              disabled={isUploading}
            />
          </Button>
          
          {existingUrl && (
            <Button 
              variant="danger" 
              onClick={handleDelete}
              disabled={isUploading}
              className="mb-2"
            >
              <i className="bi bi-trash me-2"></i>
              Löschen
            </Button>
          )}
        </div>
      </Card.Body>
    </Card>
  );
};

export default FileUploader;
